<script lang="ts" setup>
	import StringMethods from '~/utils/character'
	import type { ListContext } from '~/stores/products/types'
	import type { DropdownItemContext } from '~/components/ui/dropdown-item.vue'
	import { type ProductPlatformKeys } from '~/server/type'
	import { rename } from '~/utils/functions'

	const { platform = 'business', ...props } = defineProps<{
		productName: string | number
		productCategory: string
		variant?: 'one' | 'two'
		isSendcare?: boolean
		viewOnly?: string
		show: boolean
		platform?: ProductPlatformKeys
		tab?: string
		underwriter?: string
	}>()

	defineEmits<{
		(event: 'update:show', payload: boolean): void
	}>()
	const { productCategoryType } = useUtils()
	const { getBenefits, getProductInformation } = useContentApis()
	const { allProduct, getProductsByProductName, getProductsByCategory } = useProductLogic(platform || '')
	const { toLower } = StringMethods()
	const productNameHighlight = shallowRef<number | string>('')
	const benefitTemplate = useTemplateRef('benefits')
	const listTemplate = useTemplateRef('list')
	const dropdownTemplate = useTemplateRef('dropdown-content')
	const selectedProduct = computed(() => {
		return allProduct.value?.find(item => String(item.code_name) === String(productNameHighlight.value)) || null
	})

	const categoryType = computed(() =>
		productCategoryType(
			props.productCategory,
			String(selectedProduct.value?.code_name ?? selectedProduct.value?.name)
		)
	)
	const { generatePDFBlob } = useGeneratePDF()
	const productKeys = computed(() => Object.keys(getProductsByProductName.value))

	const productNamesCollections = computed(() => {
		if (categoryType.value?.isHealth) {
			return productKeys.value
				.map(key => getProductsByProductName.value[key])
				.filter(
					item =>
						toLower(item.category) === toLower(selectedProduct.value?.productCategory.name) &&
						item.alternate_name
				)
		} else if (categoryType.value?.isAuto) {
			return getProductsByProductName.value[props.productCategory]?.list || []
		} else if (getProductsByProductName.value[props.productCategory]?.list) {
			return getProductsByProductName.value[props.productCategory]?.list?.filter(
				item => item.code_name === props.productName
			)
		}
	})

	const addProductIcon = (code_name: number, groupKey: string) => {
		const category = selectedProduct.value?.productCategory?.name ?? ''

		let bgColorClass = `bg-${category}-500`
		let iconColorClass = `fill-${category}-500`
		let productCode = code_name
		let dir = 'fill'
		let iconProp = {
			name: 'heart-beat',
			active: '',
			inactive: ''
		}
		// Add colors and classes icons
		if (
			[
				ProductCode.FLEXICARE_BASTION_BUSINESS,
				ProductCode.FLEXICARE_HYGEIA_BUSINESS,
				ProductCode.FLEXICARE_BASTION_PERSONAL,
				ProductCode.MINI_COMPREHENSIVE
			].includes(productCode)
		) {
			bgColorClass = 'bg-travel-500'
			iconColorClass = 'fill-travel-500'
		} else if (
			[
				ProductCode.ZENCARE_BASTION_PERSONAL,
				ProductCode.ZENCARE_BASTION_BUSINESS,
				ProductCode.PRIMECARE_BASTION_PERSONAL,
				ProductCode.PRIMECARE_BASTION_BUSINESS,
				ProductCode.COMPREHENSIVE_AUTO
			].includes(productCode)
		) {
			bgColorClass = 'bg-gadget-500'
		}

		switch (toLower(category)) {
			case 'auto':
				iconProp.name = 'bus-gray'
				iconProp.active = 'bus-purple'
				iconColorClass = ''
				iconProp.inactive = 'bus-gray'
				break
			case 'gadget':
				iconProp.name = 'gadget-gray'
				iconProp.active = 'gadget-active'
				iconProp.inactive = 'gadget-gray'
				break
			case 'travel':
				iconProp.name = 'plane-gray'
				iconProp.active = 'plane-active'
				iconProp.inactive = 'plane-gray'
				break

			default:
				iconProp.name = 'heart-beat'
				iconProp.active = 'heart-beat'
				iconProp.inactive = 'heart-gray'
				break
		}
		if (selectedProduct.value?.code_name === code_name) {
			iconProp.name = iconProp.active
		} else {
			iconColorClass = 'fill-gray-400'
		}
		if (props.isSendcare) {
			dir = 'product-icons'
			if (groupKey === 'senior') {
				iconProp.name = 'seniorplan'
			} else {
				iconProp.name = 'senior-prime'
			}
			iconColorClass = ''
		}
		return {
			bgColorClass,
			iconColorClass,
			dir,
			iconProp
		}
	}
	const tabIndex = shallowRef('benefits')
	const contents = ref<Record<string, ListContext[string] | DropdownItemContext>>({
		how_it_works: [] as ListContext[string],
		exclusions: [] as DropdownItemContext,
		hospital_guide: {} as ListContext[string],
		claim_process: [] as ListContext[string],
		benefits: {} as ListContext[string],
		need_to_know: [] as ListContext[string]
	})
	const contentTopics = computed(() => [
		{
			label: 'Benefits',
			key: 'benefits',
			active: true,
			disabled: false,
			iconProps: {
				icon: 'money-transfer',
				dir: 'random'
			},
			description: '📌 Here are some major details you need to know about the product.'
		},
		{ label: 'Hospital List', key: 'hospital', active: false, disabled: !categoryType.value?.isHealth },
		{
			label: 'Need to know',
			key: 'need_to_know',
			active: false,
			disabled: false,
			description: '	📌 Here are some major details you need to know about the product.',
			iconProps: {
				icon: 'note-lg',
				dir: 'random'
			}
		},
		{
			label: 'How it works',
			key: 'how_it_works',
			active: false,
			disabled: false,
			trail: true,
			description: "📌 Here's how to get covered by us",
			iconProps: {
				icon: 'umbrella-lg',
				dir: 'fill'
			}
		},
		{
			label: 'Exclusions',
			key: 'exclusions',
			active: false,
			disabled: !categoryType.value?.isHealth,
			description: '📌 The following are excluded when you purchase the plan',
			dropdown: true,
			iconProps: {
				icon: 'disabled',
				dir: 'random',
				height: 300,
				width: 300
			}
		},
		{
			label: 'Accessing care at the hospital',
			key: 'hospital_guide',
			active: false,
			disabled: !categoryType.value?.isHealth,
			description: '📌 Here are things to note when accessing care at the hospital',
			iconProps: {
				icon: 'hospital-card',
				dir: 'fill'
			}
		},
		{
			label: 'How to claim',
			active: false,
			key: 'claim_process',
			disabled: !categoryType.value?.isAuto && !categoryType.value?.isGadget && !categoryType.value?.isTravel,
			trail: true,
			description: "📌 Here's how to make a claim",
			iconProps: {
				icon: 'money-transfer',
				dir: 'random'
			}
		}
	])

	const selectedProductList = computed(() => {
		const productName = categoryType.value?.isHealth
			? toLower(selectedProduct.value?.group_id)
			: toLower(selectedProduct.value?.form_name)
		const collection = getProductsByProductName.value?.[productName] ?? null
		if (collection?.list && collection.list?.length > 1) {
			return collection?.list ?? []
		} else if (props.productCategory && !categoryType.value?.isHealth) {
			return getProductsByCategory.value?.[String(props.productCategory)] ?? []
		}
		return []
	})
	const activeTab = computed(() => contentTopics.value.find(item => tabIndex.value === item.key) ?? null)
	const contentStore = computed(() => {
		if (activeTab.value?.key === 'need_to_know') {
			return contents.value.need_to_know ?? []
		} else if (activeTab.value?.key === 'how_it_works') {
			return contents.value.how_it_works || []
		} else if (activeTab.value?.dropdown) {
			return contents.value.exclusions || []
		} else if (activeTab.value?.key === 'hospital_guide') {
			return contents.value.hospital_guide || []
		} else if (activeTab.value?.key === 'claim_process') {
			return contents.value.claim_process || []
		} else {
			return contents.value.benefits || []
		}
	})
	const excludeList = computed(() => {
		if (selectedProduct.value?.code_name === ProductCode.MINI_COMPREHENSIVE) {
			return [ProductCode.THIRD_PARTY]
		}
		return []
	})
	const downloading = shallowRef(false)
	const selectTab = (index: string) => {
		tabIndex.value = index
	}
	const requestContents = async () => {
		const key = activeTab.value?.key ?? ''
		if (key) {
			await resolve(async () => {
				if (
					(!categoryType.value?.isHealth && !categoryType.value?.isAuto && !categoryType.value?.isTravel) ||
					categoryType.value?.isComprehensive
				) {
					const benefits = await getBenefits({
						category: selectedProduct.value?.productCategory.name ?? '',
						code_name: selectedProduct.value?.code_name ?? 0,
						user_type: platform,
						group_id: selectedProduct.value?.group_id ?? ''
					})
					if (benefits.value?.data) {
						if (categoryType.value?.isGadget || categoryType.value?.isComprehensive) {
							contents.value.benefits = (benefits.value.data || []) as ListContext[string]
						}
					}
				}
				const productInformation = await getProductInformation({
					category: selectedProduct.value?.productCategory.name ?? '',
					code_name: selectedProduct.value?.code_name ?? '',
					user_type: platform
				})
				if (productInformation?.data) {
					Object.keys(contents.value).forEach(key => {
						if (productInformation.data?.[key]) {
							contents.value[key] = productInformation.data[key]
						}
					})
				}
			})
		}
	}
	const openProduct = (arg: number) => {
		productNameHighlight.value = arg
	}
	const downloadTab = async (content: any, opt?: { name?: string; format?: string; orientation?: string }) => {
		const { format, orientation, name } = { name: 'file', format: 'letter', orientation: 'portrait' }
		downloading.value = true
		try {
			await generatePDFBlob(content, {
				filename: opt?.name ?? name,
				jsPDF: {
					unit: 'in',
					filename: opt?.name ?? name,
					format: opt?.format ?? format,
					orientation: opt?.orientation ?? orientation,
					hotfixes: ['px_scaling']
				}
			})
		} catch (e) {
			console.error(e)
			useNotification().alert({
				status: 400,
				message: 'Something wrong downloading file'
			})
		} finally {
			downloading.value = false
		}
	}
	const downloadContent = () => {
		if (activeTab.value?.key === 'benefits' && benefitTemplate.value?.template) {
			downloadTab(benefitTemplate.value.template, {
				name: `${selectedProduct.value?.name ?? ''} - Benefits`,
				orientation: 'landscape',
				format: 'tabloid'
			})
		} else if (activeTab.value?.dropdown && dropdownTemplate.value?.template) {
			downloadTab(dropdownTemplate.value.template, {
				name: `${selectedProduct.value?.name ?? ''} - ${activeTab.value?.label ?? 'File'}`
			})
		} else if (listTemplate.value?.template) {
			downloadTab(listTemplate.value.template, {
				name: `${selectedProduct.value?.name ?? ''} - ${activeTab.value?.label ?? 'File'}`
			})
		} else {
			useNotification().alert({
				status: 400,
				message: 'This download is not available'
			})
		}
	}

	watch(
		() => [props.productName, props.show],
		() => {
			if (props.show) {
				if (allProduct.value.some(item => item.group_id === String(props.productName))) {
					productNameHighlight.value =
						allProduct.value.find(item => item.group_id === String(props.productName))?.code_name ?? 0
				} else if (allProduct.value.some(item => String(item.code_name) === String(props.productName))) {
					productNameHighlight.value =
						allProduct.value.find(item => String(item.code_name) === String(props.productName))
							?.code_name ?? 0
				}

				if (props.tab) {
					tabIndex.value = props.tab
				} else {
					tabIndex.value = 'benefits'
				}
				requestContents()
			}
		}
	)

	onMounted(() => {
		requestContents()
	})
	watch(activeTab, () => {
		if (activeTab.value?.key) {
			requestContents()
		}
	})
</script>

<template>
	<ui-modal
		:show="show"
		class="sm:p-0 p-0"
		:ui="{ rounded: variant == 'two' ? 'rounded-none' : 'rounded-lg' }"
		@update:show=";[$emit('update:show', false)]"
	>
		<div>
			<nav
				v-if="!isSendcare"
				class="md:h-[80px] border-b sm:px-8 px-3 py-3 gap-5 flex flex-wrap items-center justify-between"
			>
				<tools-icon dir="internal-logo" icon="mcg-logo-black" width="145" height="36" />
				<u-button
					color="white"
					:loading="downloading"
					variant="ghost"
					type="button"
					class="text-main inline-flex items-center gap-x-3 font-semibold text-xs sm:text-base"
					@click="downloadContent"
				>
					<span v-show="!downloading">
						<tools-icon dir="line" icon="download" svg-class="sm:w-[24px] sm:h-[24px] h-[18px] w-[18px]" />
					</span>
					<span>Download as PDF</span>
				</u-button>
			</nav>
			<nav v-else class="lg:sticky top-0 left-0 z-[600]">
				<img
					class="w-full max-w-[1440px] object-contain"
					src="/img/logos/sendcare.svg"
					loading="lazy"
					alt="sendcare"
				/>
			</nav>

			<section class="mt-5 sm:px-8 px-4">
				<!--==========> Actions======= -->
				<!-- Product List -->
				<div
					v-if="!viewOnly || viewOnly === 'hospital'"
					class="w-full border-b bg-white border-gray-300 overflow-x-auto flex gap-x-5 pt-6 items-center"
				>
					<button
						v-for="item in productNamesCollections"
						:key="'product-btn-' + item.code_name"
						class="gap-x-2 btn maitree-semibold flex-none custom-transition-2 md:text-2xl px-8 sm:min-w-[300px] h-[40px] sm:h-[57px] inline-flex items-center capitalize mx-auto border-b-2 border-transparent justify-center"
						:class="{
							active: selectedProduct?.code_name === item.code_name,
							'bg-[#eefbf6]': variant == 'two' && selectedProduct?.code_name === item.code_name
						}"
						type="button"
						name="product item"
						@click="openProduct(item.code_name)"
					>
						<tools-icon
							:icon="addProductIcon(item.code_name, item.group_id ?? '').iconProp.name"
							:dir="addProductIcon(item.code_name, item.group_id ?? '').dir"
							:height="24"
							:width="24"
							:path-class="`${
								addProductIcon(item.code_name, item.group_id ?? '').iconColorClass
							} custom-transition-2`"
						/>
						<span>
							{{
								toLower(item?.group_id) === 'none'
									? item.alternate_name
									: rename(item?.group_id || 'N/A', ['senior', 'care'], ['SeniorPlan', 'Care'])
							}}
						</span>
					</button>
				</div>
				<!--  -->

				<div
					v-if="!viewOnly"
					class="flex justify-center lg:items-center items-start flex-col w-full overflow-x-auto overflow-hidden"
				>
					<div
						class="rounded-[40px] inline-flex items-center gap-x-5 mt-4 px-3 py-2 text-ellipsis"
						:class="[variant === 'two' ? 'bg-[#FFF2EB] border-[#FE7D35]' : 'bg-gray-100']"
					>
						<template v-for="(item, index) in contentTopics" :key="`benefit-key-${index}`">
							<button
								v-if="!item.disabled"
								:class="[
									item.key === activeTab?.key
										? [variant === 'two' ? 'bg-[#FE7D35] text-white' : 'bg-white ', 'font-[700]']
										: 'text-gray-600 font-medium'
								]"
								:name="item.label"
								class="px-3 py-1 sm:p-[6px_28px] sm:text-base text-sm rounded-[20px] whitespace-pre"
								type="button"
								@click="selectTab(item.key)"
							>
								{{ item.label }}
							</button>
						</template>
					</div>
				</div>
				<!-- ================/End========== -->

				<div v-if="show" class="my-10 overflow-x-auto lg:overflow-visible">
					<transition-group
						name="animate"
						enter-active-class="animate__animated animate__fadeIn animate__faster"
					>
						<!--========= Benefit card======= -->
						<div
							name="health-benefit"
							v-if="
								activeTab?.key === 'benefits' &&
								((categoryType?.isAuto && !categoryType.isComprehensive) ||
									categoryType?.isHealth ||
									categoryType?.isTravel)
							"
							class="w-full"
						>
							<molecules-benefit-skin
								:product-code="Number(selectedProduct?.code_name ?? '')"
								:product-category="
									toLower(selectedProduct?.productCategory.name ?? props.productCategory ?? '')
								"
								:product-group-id="selectedProduct?.group_id ?? ''"
								:product-list="selectedProductList"
								:sort="true"
								:exclude="excludeList"
								:sticky-y-offset="0"
								:platform
								:provider="underwriter || selectedProduct?.underwriter || ''"
								:is-diaspora="isSendcare"
								:nav-link-cls="
									variant === 'two'
										? 'border-[#6FD9BB] hover:border-transparent bg-[#E2F7F1] hover:bg-green-deep hover:text-white text-green-deep'
										: ''
								"
								ref="benefits"
							/>
						</div>

						<template v-else-if="activeTab?.key === 'hospital'">
							<lazy-molecules-hospital-list
								:is-sendcare="isSendcare"
								:product-name="selectedProduct?.alternate_name ?? ''"
								:group-key="categoryType?.isHealth ? String(selectedProduct?.group_id ?? '') : ''"
								:platform
							/>
						</template>
						<template v-else>
							<div class="flex items-start justify-between gap-x-3">
								<div class="icon-wrapper lg:inline hidden">
									<tools-icon
										v-if="activeTab?.iconProps"
										height="300"
										width="300"
										v-bind="activeTab.iconProps"
									/>
								</div>
								<div
									class="max-w-[710px] w-full border px-3 py-3.5 sm:p-8 min-h-[500px] max-h-[800px] overflow-y-auto"
									:class="[
										variant === 'two'
											? 'bg-[#FFF2EB] border-[10px] border-white rounded-lg'
											: 'bg-white'
									]"
								>
									<ui-list-display
										v-if="!activeTab?.dropdown"
										ref="list"
										:list="contentStore"
										class="mt-6 relative"
										:caption="activeTab?.description ?? ''"
										:trail="activeTab?.trail ?? false"
										:list-style-cls="
											variant === 'two'
												? 'bg-[#FFE4DA] border-[#FE7D35] text-[#FE7D35] shadow-[0_0_10px_0_rgba(254,125,53,0.23),0_0_10px_0_rgba(254,125,53,0.2)]'
												: ''
										"
										:list-cls="variant === 'two' ? 'before:border-[#FE7D35]' : ''"
									/>
									<ui-dropdown-item
										v-else
										ref="dropdown-content"
										:caption="activeTab?.description ?? ''"
										:list="contentStore as DropdownItemContext"
										class="mt-6 relative"
										:trail="activeTab?.trail ?? false"
									/>
								</div>
							</div>
						</template>
					</transition-group>
				</div>
			</section>
		</div>
	</ui-modal>
</template>

<style scoped>
	.btn {
		@apply text-gray-500;
	}
	:deep(.w) {
		width: min(100vw, 1440px);
	}

	.btn.active {
		@apply border-main text-gray-800;
	}

	.btn :deep(.icon-path) {
		@apply fill-gray-400;
	}
</style>
